import type { FactorLibrarySet } from "../api/factors";
import { publicationComparison } from "../lib/factorComparison";
import { shortDate } from "../lib/format";
import { EPA_GRID_COMPARISON_2025 as source } from "../reference/epa-grid-comparison-2025";
import own from "./FactorLibrary.module.css";
import styles from "./Screen.module.css";

/** A separate source comparison beside returned DB records, never extra library rows. */
export function FactorPublicationComparison({ set }: { set: FactorLibrarySet }) {
  // Eligible by what the set *is* - the publication's edition, publisher and GWP basis, locked - and
  // by whether it is the edition that prices new figures. It was keyed on one set's key, which became
  // the retired recording when ADR-0197 added `us_epa_2025_v2`: the comparison then sat on the edition
  // kept for history and was absent from the one a reader prices with. A key is a recording; the
  // edition string is the publication.
  if (set.jurisdiction !== source.jurisdiction) return null;
  const eligible = set.prices_new_figures && set.edition === source.setEdition &&
    set.publisher === source.publisher && set.gwp_set_label === source.gwpSet && set.locked;
  const rows = eligible ? source.rows.filter((row) => set.factors.some((factor) =>
    factor.category === "electricity" && factor.fuel_or_carrier === "grid_electricity" &&
    factor.region === row.region && factor.unit_in === "kWh" && factor.unit_out === "kgCO2e" &&
    factor.publisher === source.publisher && factor.edition === source.setEdition &&
    factor.external_id === `eGRID2023rev2-${row.region}` && factor.verification_row === "C-10a",
  )) : [];
  return <section aria-label={`EPA grid publication comparison, ${set.label}`} data-testid="factor-publication-comparison" data-comparison-id={source.id}>
    <h3>Why the Hub electricity numbers differ</h3>
    <p className={styles.note} data-testid="factor-comparison-reason">
      EmitIQ uses eGRID2023 rev2, the later revised EPA publication, as its authoritative US grid basis.
      EF Hub 2025 Table 6 cites January 2025 eGRID.
      This separate publication comparison does not select or load an alternative factor.
    </p>
    {rows.length === 0 ? <p className={styles.note} data-testid="factor-comparison-unavailable">
      No verified publication comparison matches this returned set’s edition, GWP basis and electricity rows.
    </p> : <>
      <p className={styles.note} data-testid="factor-comparison-matched-basis">
        The matching locked electricity rows above use the authoritative eGRID2023 rev2 basis
        and are the database records used by figure receipts.
      </p>
      <p className={styles.note} id={`${source.id}-basis`}>
        Total output only, in lb CO₂e/MWh on AR5 100-year GWPs (CO₂ 1, CH₄ 28, N₂O 265).
        Hub CO₂e is calculated from rounded Table 6 components, not a published CO₂e total.
        Rev2 is its Table 1 PDF’s rounded CO₂e column. Both are shown to one decimal;
        differences are approximate. A displayed 0.0 means the difference rounds to zero, not that the publications are identical.
        No non-baseload or grid-loss adjustment is included.
      </p>
      <div className={own.scroll} tabIndex={0} role="region" aria-label="Publication comparison table, horizontally scrollable">
        <table className={own.table} aria-describedby={`${source.id}-basis`} data-testid="factor-comparison-table">
          <caption className={own.caption}>EPA publication comparison · lb CO₂e/MWh</caption>
          <thead><tr>
            <th scope="col">Region</th><th scope="col">eGRID2023 rev2<br />published CO₂e</th>
            <th scope="col">EF Hub 2025<br />derived CO₂e ≈</th><th scope="col">Hub − rev2 ≈</th>
          </tr></thead>
          <tbody>{rows.map((row) => {
            const result = publicationComparison(row, source.gwp);
            return <tr key={row.region} data-comparison-region={row.region}>
              <th scope="row">{row.region}</th><td className={own.num}>{row.rev2}</td>
              <td className={own.num}>{result.hub}</td><td className={own.num}>{result.difference}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>
      <p className={styles.note}>Only regions with matching returned electricity rows are compared.</p>
      <p className={styles.note} data-testid="factor-comparison-provenance">
        {source.publisher} · source checked · retrieved {shortDate(source.retrievedAt)}. Sources: {" "}
        <a href={source.hubSource} target="_blank" rel="noopener noreferrer">{source.hubEdition}, Table 6</a>; {" "}
        <a href={source.hubGwpSource} target="_blank" rel="noopener noreferrer">Hub GWP table, page 1</a>; {" "}
        <a href={source.rev2Source} target="_blank" rel="noopener noreferrer">{source.rev2Edition}, Table 1</a>.
      </p>
      <p className={styles.note} data-testid="factor-comparison-calculation">
        Calculation: Hub CO₂e = CO₂ + 28 × CH₄ + 265 × N₂O. All input components below are lb/MWh, at published precision:
        {rows.map((row) => <span key={row.region}> {row.region}: CO₂ {row.co2}, CH₄ {row.ch4}, N₂O {row.n2o}.</span>)}
      </p>
    </>}
  </section>;
}
